import { PlayIcon } from 'lucide-react';

import { Pill } from '@/components/ui';
import { cn } from '@/lib/utils';

// List of 7 dark background colors for rotation
const RECORDING_COLORS = [
  'bg-deep-river-dark',
  'bg-prickly-pink-dark',
  'bg-orange-persimmon-dark',
  'bg-patrice-green-dark',
  'bg-nirvana-jewel-dark',
  'bg-cold-lips-dark',
  'bg-warning-yellow-dark',
] as const;

export type RecordingInfoPill = {
  text: string;
  options?: {
    color?: string;
  };
};

interface SessionRecordingProps {
  sessionTitle: string;
  onClick?: () => void;
  moduleIcon?: string;
  colorIndex?: number;
  infoPills?: RecordingInfoPill[];
}

export default function SessionRecording({
  sessionTitle,
  moduleIcon,
  onClick,
  colorIndex = 0,
  infoPills = [],
}: Readonly<SessionRecordingProps>) {
  const bgColor = RECORDING_COLORS[colorIndex % RECORDING_COLORS.length]; // Get color based on index (cycles through 7 colors)

  return (
    <div
      className={`${bgColor} rounded-2xl p-2 h-44 w-78 flex items-center gap-3 cursor-pointer hover:shadow-2xl hover:scale-101 transition-colors duration-200 group`}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Video placeholder area */}
      <div className="flex-shrink-0 w-24 h-full bg-magical-moonlight-dark/30 rounded-lg flex items-center justify-center relative overflow-hidden transition-colors duration-200">
        {moduleIcon ? (
          <img src={moduleIcon} alt="Module Icon" className="w-25 h-25" />
        ) : (
          <div className="text-magical-moonlight-white/60 text-xs font-medium">
            Play here
          </div>
        )}

        {/* Play button overlay on hover */}
        <div className="absolute inset-0 bg-magical-moonlight-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <PlayIcon className="h-6 w-6 text-magical-moonlight-white" />
        </div>
      </div>

      {/* Lesson details */}
      <div className="flex-1 text-magical-moonlight-white">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {infoPills?.map((pillDetails, index) =>
            pillDetails.text ? (
              <Pill
                key={index}
                rounded
                variant="outline"
                size="sm"
                className={cn(
                  'border-none bg-magical-moonlight-white/10 px-4 py-1',
                  pillDetails.options?.color ?? ''
                )}
              >
                {pillDetails.text}
              </Pill>
            ) : null
          )}
        </div>
        <h3 className="text-lg font-semibold leading-tight line-clamp-3">
          {sessionTitle}
        </h3>
      </div>
    </div>
  );
}
